import type { SupabaseClient } from "@supabase/supabase-js";

// Member directory read model over public.management_member_directory (member / role / status
// directory; member_display_name + member_ref; management-scoped RLS). Read-only list;
// display_name updates via update_account_member_display_name RPC.

export interface MemberRow {
  membershipId: string;
  memberRef: string | null;
  memberDisplayName: string | null;
  memberLabel: string | null;
  memberEmail: string | null;
  contactPhone: string | null;
  role: string;
  status: string;
  createdAt: string | null;
  updatedAt: string | null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

export function mapMemberRow(raw: Record<string, unknown>): MemberRow {
  return {
    membershipId: asString(raw.membership_id) ?? "",
    memberRef: asString(raw.member_ref),
    memberDisplayName: asString(raw.member_display_name),
    memberLabel: asString(raw.member_label),
    memberEmail: asString(raw.member_email),
    contactPhone: asString(raw.contact_phone),
    role: asString(raw.role) ?? "member",
    status: asString(raw.status) ?? "active",
    createdAt: asString(raw.created_at),
    updatedAt: asString(raw.updated_at),
  };
}

export const MEMBER_LIMIT = 100;

export const SAFE_MEMBER_PROFILE_FAILURE =
  "Couldn't save that member profile. Please try again.";

const KNOWN_MEMBER_PROFILE_FAILURES: ReadonlySet<string> = new Set([
  "Member management permission is required.",
  "Display name is required.",
  "Display name is too long.",
  "Contact phone is not valid.",
  "Membership was not found for this account.",
]);

export type MemberProfileOutcome =
  | { ok: true }
  | { ok: false; reason: string };

/** @deprecated use updateMemberProfile */
export const SAFE_MEMBER_DISPLAY_NAME_FAILURE = SAFE_MEMBER_PROFILE_FAILURE;

/** Fetch the member directory, most recently added first. Throws on read error. */
export async function fetchMembers(
  client: SupabaseClient,
  accountId: string,
  limit: number = MEMBER_LIMIT,
): Promise<MemberRow[]> {
  const { data, error } = await client
    .from("management_member_directory")
    .select("*")
    .eq("account_id", accountId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error !== null) {
    throw new Error("Could not load the member directory.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((row) => mapMemberRow(row as Record<string, unknown>));
}

export async function updateMemberProfile(
  client: SupabaseClient,
  accountId: string,
  membershipId: string,
  displayName: string,
  contactPhone: string | null = null,
): Promise<MemberProfileOutcome> {
  try {
    const { error } = await client.rpc("update_account_member_profile", {
      p_account_id: accountId,
      p_membership_id: membershipId,
      p_display_name: displayName.trim(),
      p_contact_phone: contactPhone?.trim() ? contactPhone.trim() : null,
    });
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return {
        ok: false,
        reason: KNOWN_MEMBER_PROFILE_FAILURES.has(message)
          ? message
          : SAFE_MEMBER_PROFILE_FAILURE,
      };
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: SAFE_MEMBER_PROFILE_FAILURE };
  }
}

export async function updateMemberDisplayName(
  client: SupabaseClient,
  accountId: string,
  membershipId: string,
  displayName: string,
): Promise<MemberProfileOutcome> {
  return updateMemberProfile(client, accountId, membershipId, displayName, null);
}

export const SAFE_MEMBER_ROLE_FAILURE = "Couldn't save that member role. Please try again.";

const KNOWN_MEMBER_ROLE_FAILURES: ReadonlySet<string> = new Set([
  "Member management permission is required.",
  "Role must be guard, supervisor, or admin.",
  "Membership was not found for this account.",
  "You cannot change your own role.",
  "At least one active admin is required for this account.",
]);

export type MemberRoleOutcome = { ok: true } | { ok: false; reason: string };

export async function updateMemberRole(
  client: SupabaseClient,
  accountId: string,
  membershipId: string,
  role: string,
): Promise<MemberRoleOutcome> {
  try {
    const { error } = await client.rpc("update_account_member_role", {
      p_account_id: accountId,
      p_membership_id: membershipId,
      p_role: role,
    });
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return {
        ok: false,
        reason: KNOWN_MEMBER_ROLE_FAILURES.has(message) ? message : SAFE_MEMBER_ROLE_FAILURE,
      };
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: SAFE_MEMBER_ROLE_FAILURE };
  }
}
